import { inject, InjectionToken } from '@angular/core';
import type { ApiTypes } from '../..';

export function mapModelsForTracking(models: ModelTracking[]): Array<{ name: string }> {
    return models.map((m) => {
        return {
            name: m.name,
        };
    });
}

export function mapVehicleForTracking(
    vehicles: ApiTypes.VehicleDetail[],
): Array<{ basePrice: string; name: string; year: string }> {
    return vehicles.map((v) => {
        return {
            basePrice: v.price.buy.toString(),
            name: v.model ?? '',
            year: v.firstRegistration?.slice(0, 4) ?? '',
        };
    });
}

export function mapDealerFromVehicleForTracking(vehicle: ApiTypes.VehicleDetail): {
    name: string;
    code: string;
    zipcode: string;
    location: string;
} {
    const d = vehicle.dealer;
    return {
        name: d.name,
        code: d.id,
        zipcode: d.location.zip,
        location: d.location.city,
    };
}

export function filterTargetTracking(value: string): string {
    if (value.includes('mailto:')) {
        return 'email';
    } else if (value.includes('tel:')) {
        return 'phone call';
    }

    return value;
}

export type DataLayerValue = string | number | undefined | DataLayerEntry | DataLayerEntry[] | object[] | boolean;
export interface DataLayerEntry {
    [key: string]: DataLayerValue;
}
export type DataLayer = DataLayerEntry[];

export enum PageComponentName {
    COMPARISON = 'comparison',
    CONFIGURATOR = 'configurator',
    CONTACT = 'contact',
    DEALERSEARCH = 'dealer search',
    EVTOOLS = 'ev-tools',
    HLIVEASSISTANT = 'h-live-assistant',
    OFFERQUOTE = 'offer/quote',
    TESTDRIVE = 'testdrive',
    TRADEIN = 'tradeIn',
}

export enum PageContentType {
    HOME = 'Home Page',
    ABOUTUS = 'About us Page',
    MODELOVERVIEW = 'Model Overview Page',
    MODELDETAIL = 'Model Detail Page',
    SERVICE = 'Service Page',
    SERVICEFORM = 'Service Request Form Page',
    SHOP = 'Shop Page',
    CAMPAIGNOVERVIEW = 'Campaign Overview Page',
    CAMPAIGNDETAIL = 'Campaign Detail Page',
    WARRENTY = 'Warrenty Page',
    PROMISE = 'Promise Page',
    BUSINESSCUSTOMER = 'Business Customer Page',
    IMRPINT = 'Imprint Page',
    LEGAL = 'Legal Page',
    PRIVACY = 'Privacy Page',
    DEALERCAMPAIGNDETAIL = 'Dealer Campaign Detail Page',
    VEHICLELIST = 'Vehicle Search Results Page',
    VEHICLEDETAIL = 'Vehicle Detail Page',
    CARMARKETFORM = 'Hyundai Car Market Form Page',
    CONTACTFORM = 'Contact Form Page',
    SEARCHABO_VERIFY = 'SearchAbo Verify Page',
    SEARCHABO_CANCEL = 'SearchAbo Cancel Page',
    SEARCHABO_ERROR = 'SearchAbo Error Page',
}

export enum ApplicationName {
    WWN = 'WWN',
    CLICKTOBUY = 'click-to-buy',
    MYHYUNDAI = 'myhyundai',
    STOCKLOCATOR = 'stock-locator',
    MOCEAN = 'mocean',
    HLIVECONSULTATION = 'h-live-consultation',
    HWS = 'dealers',
    HCM = 'used car market',
}

const TrackingElementTypes = [
    'accordion',
    'action button',
    'anchor link',
    'basic link',
    'button',
    'colour picker',
    'dy nudge',
    'exterior 360 view',
    'footer',
    'hotspot',
    'image',
    'interior 360 view',
    'menu',
    'navigation bar',
    'slider',
    'sound',
    'tab',
    'teaser',
    'banner',
] as const;
export type TrackingElementType = (typeof TrackingElementTypes)[number];

type TabStatus<N extends number> = `tab_${N}`;
type TrackingPageElementStatusTypes<N extends number = 1> = ['closed', 'off', 'on', 'opened', TabStatus<N>];
export type TrackingPageElementStatusType<N extends number = 1> = TrackingPageElementStatusTypes<N>[number];

export type TrackingEvent = 'engagement_click' | 'element_click' | 'element_change';

export interface ModelTracking {
    name: string;
}

export interface PageloadStartLayer extends DataLayerEntry {
    event: 'pageload_start';
    environment: {
        type: string;
        country: string;
        datalayerVersion: string;
    };
    page: {
        name: string;
        contentType: string;
        language: string;
        application: {
            name: ApplicationName;
            version: string;
        };
        componentName: string | undefined;
    };
    dealer?: {
        name: string;
        zipcode: string;
        location: string;
        code: string;
    };
}

const trackingEnvInjectionToken = new InjectionToken<string>('tracking-env');

export const [injectTrackingEnv, provideTrackingEnv] = [
    () => inject(trackingEnvInjectionToken),
    (env: string) => ({
        provide: trackingEnvInjectionToken,
        useValue: env,
    }),
];
